import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/shared/PageHero";
import CoteIvoireMap from "@/components/projets/CoteIvoireMap";
import { useLang } from "@/i18n/LanguageContext";
import { PROJECTS, PROJECT_TYPE_COLOR } from "@/data/ekipData";

const HERO_IMG =
  "https://images.unsplash.com/photo-1518709594023-6ebbf05c62dc?auto=compress&cs=tinysrgb&fm=jpg&w=1600&q=80";

export default function Projets() {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState("all");
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  const filtered = filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.type === filter);

  return (
    <div data-testid="projets-page">
      <PageHero
        overline={t.projets.hero.overline}
        title={t.projets.hero.title}
        titleItalic={t.projets.hero.titleItalic}
        subtitle={t.projets.hero.subtitle}
        image={HERO_IMG}
      />

      {/* Map */}
      <section className="bg-ekip-cream py-20 md:py-32" data-testid="projets-map">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-14">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.projets.map.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.projets.map.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.projets.map.titleItalic}</em>
            </h2>
          </div>

          {/* Filters */}
          <div className="mb-8 flex flex-wrap items-center gap-3">
            {["all", "immobilier", "energie", "tourisme", "infrastructure"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`projets-filter-${f}`}
                className={`text-[11px] uppercase tracking-[0.24em] font-medium px-4 py-2 border transition-colors ${
                  filter === f
                    ? "bg-ekip-navy text-white border-ekip-navy"
                    : "bg-white text-ekip-navy border-ekip-gold/30 hover:border-ekip-copper hover:text-ekip-copper"
                }`}
              >
                {f === "all" ? t.carrieres.offers.all : t.projets.map.legend[f]}
              </button>
            ))}
          </div>

          <CoteIvoireMap projects={filtered} />
        </div>
      </section>

      {/* Project cards detailed */}
      <section className="bg-white py-20 md:py-32 border-y border-ekip-gold/20" data-testid="projets-list">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16">
            <div className="col-span-12 md:col-span-4 flex items-start gap-3">
              <span className="diamond mt-2" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-ekip-copper font-medium">
                {t.projets.list.eyebrow}
              </span>
            </div>
            <h2 className="col-span-12 md:col-span-8 font-display font-semibold text-3xl md:text-5xl leading-[1.05] text-ekip-navy">
              {t.projets.list.title}
              <br />
              <em className="italic font-light text-ekip-copper">{t.projets.list.titleItalic}</em>
            </h2>
          </div>

          <div className="space-y-6">
            {PROJECTS.map((p, i) => {
              const color = PROJECT_TYPE_COLOR[p.type];
              return (
                <motion.article
                  key={p.slug}
                  id={p.slug}
                  data-testid={`project-detail-${p.slug}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, delay: i * 0.05 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-0 border border-ekip-gold/20 hover:border-ekip-copper transition-colors overflow-hidden"
                >
                  <div className="md:col-span-5 relative min-h-[280px]">
                    <div
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${p.image})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-ekip-navy/70 to-transparent" />
                    <span
                      className="absolute top-5 left-5 inline-flex items-center gap-2 text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-medium"
                      style={{ background: color }}
                    >
                      {p.badge}
                    </span>
                  </div>
                  <div className="md:col-span-7 p-8 md:p-12">
                    <span className="text-[10px] uppercase tracking-[0.3em] font-medium" style={{ color }}>
                      {t.projets.map.legend[p.type]}
                    </span>
                    <h3 className="mt-2 font-display font-bold text-3xl md:text-4xl text-ekip-navy leading-tight">
                      {p.title}
                    </h3>
                    <p className="mt-2 font-display italic text-lg text-ekip-graphite">
                      {p.subtitle[lang]}
                    </p>
                    <p className="mt-6 text-ekip-slate font-body font-light leading-relaxed">
                      {p.description[lang]}
                    </p>

                    <div className="mt-8 grid grid-cols-3 gap-4">
                      {p.kpis.slice(0, 3).map((k, ki) => (
                        <div key={ki}>
                          <div className="font-display font-bold text-xl text-ekip-copper">
                            {k.value}
                          </div>
                          <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ekip-graphite">
                            {k.label[lang]}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-ekip-graphite mr-2">
                        {lang === "fr" ? "Partenaires" : "Partners"}
                      </span>
                      {p.partners.map((pr, pi) => (
                        <span key={pi} className="text-xs px-3 py-1 border border-ekip-gold/30 text-ekip-navy">
                          {pr}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ekip-navy py-20 md:py-24 text-center" data-testid="projets-cta">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14">
          <p className="font-display font-semibold text-3xl md:text-4xl lg:text-5xl text-white">
            {t.projets.cta.title}
          </p>
          <p className="mt-4 text-white/70 font-body font-light max-w-2xl mx-auto">
            {t.projets.cta.subtitle}
          </p>
          <Link to="/contact" data-testid="projets-cta-btn" className="btn-primary mt-10">
            {t.projets.cta.button}
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
